import React, { useEffect, useMemo, useState } from "react";
import { Eye } from "lucide-react";
import {
    Popover, PopoverContent, PopoverTrigger,
} from "./ui/popover";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import ModuleIdChip from "./ModuleIdChip";
import { formatDateTime } from "../lib/domain";

/**
 * Iter60.p54.2 — Officer change-diff surfaces for the Manager.
 *
 * Two consumers:
 *
 *   1) <OfficerChangeDiffPopover row /> — a small icon-button next
 *      to a Submitted row's stage chip. Opens a popover listing every
 *      pending_changes entry as before → after with actor + timestamp.
 *
 *   2) <BulkApproveReviewDialog open onOpenChange rows onConfirm /> —
 *      the review dialog that intercepts the Manager's bulk "Approve N"
 *      when at least one selected row is `stage=submitted`. Renders
 *      one card per Submitted row with a Skip toggle. Pending rows in
 *      the same selection are shown as a compact "Direct approve"
 *      section — they don't need the review step.
 */

// Human-readable field labels.
const FIELD_LABELS = {
    check_in_time: "Check-in",
    check_out_time: "Check-out",
    status: "Status",
    project_id: "Project",
    late_minutes: "Late (min)",
    ot_minutes: "Overtime (min)",
    pay_multiplier: "Pay multiplier",
    notes: "Notes",
    excused_early_departure: "Excused early departure",
    excuse_note: "Excuse note",
    early_departure_note: "Early-departure note",
    early_departure_reason_code: "Early-departure reason",
    // Iter60.p54.3 — Adjustments surface here so the Manager sees
    // an Officer's Adjust-Hours action in the diff dialog.
    adjustment_added: "Hour adjustment",
};

function labelOf(field) {
    return FIELD_LABELS[field] || field;
}

function formatValue(field, v) {
    if (v == null || v === "") return "—";
    if (typeof v === "boolean") return v ? "Yes" : "No";
    if (field === "check_in_time" || field === "check_out_time") {
        // Backend stores full ISO — show HH:MM.
        try {
            const d = new Date(v);
            if (!isNaN(d)) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
        } catch (_) {}
        return String(v);
    }
    // Iter60.p54.3 — Adjustment payload: {minutes, note}. Render as
    // "+30m — note" so the Manager sees intent inline.
    if (field === "adjustment_added" && v && typeof v === "object") {
        const m = Number(v.minutes || 0);
        const sign = m > 0 ? "+" : "";
        const note = v.note ? ` — ${v.note}` : "";
        return `${sign}${m}m${note}`;
    }
    return String(v);
}

function ChangeList({ entries }) {
    if (!entries || entries.length === 0) {
        return (
            <div className="text-xs italic text-[color:var(--asl-muted)] py-2">
                No changes recorded on this row.
            </div>
        );
    }
    return (
        <ul className="space-y-1.5" data-testid="officer-change-list">
            {entries.map((c) => (
                <li key={c.id} className="text-xs leading-relaxed"
                    data-testid={`officer-change-entry-${c.field}`}>
                    <span className="font-semibold" style={{ color: "var(--asl-navy)" }}>
                        {labelOf(c.field)}:
                    </span>{" "}
                    <span className="line-through opacity-60">{formatValue(c.field, c.before)}</span>
                    <span className="mx-1 opacity-60">→</span>
                    <span className="font-semibold" style={{ color: "var(--asl-crimson)" }}>
                        {formatValue(c.field, c.after)}
                    </span>
                    {c.changed_by_email && (
                        <div className="text-[10px] mt-0.5 opacity-70">
                            by {c.changed_by_email}
                            {c.changed_at ? ` · ${formatDateTime(c.changed_at)}` : ""}
                        </div>
                    )}
                </li>
            ))}
        </ul>
    );
}

export function OfficerChangeDiffPopover({ row }) {
    const entries = row?.pending_changes || [];
    if (!row?.submitted_by_officer_id) return null;
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center justify-center rounded-full p-0.5 hover:bg-black/5"
                    style={{ color: "var(--asl-navy)" }}
                    title="View changes made by the Officer"
                    aria-label="View Officer changes"
                    data-testid={`att-view-changes-${row.id}`}
                >
                    <Eye className="h-3.5 w-3.5" />
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-80 p-3">
                <div className="text-xs font-bold mb-1.5" style={{ color: "var(--asl-navy)" }}>
                    Officer changes
                </div>
                <ChangeList entries={entries} />
            </PopoverContent>
        </Popover>
    );
}

export function BulkApproveReviewDialog({
    open, onOpenChange, submittedRows = [], pendingRows = [],
    onConfirm, busy,
}) {
    // per-row skip toggle (default: include)
    const [skipMap, setSkipMap] = useState({});
    // Reset skip map every time the dialog reopens with new rows.
    const rowKeyList = useMemo(
        () => submittedRows.map((r) => r.id).join(","),
        [submittedRows],
    );
    useEffect(() => { setSkipMap({}); }, [rowKeyList]);
    const submittedIncluded = submittedRows.filter((r) => !skipMap[r.id]);
    const total = submittedIncluded.length + pendingRows.length;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-3xl max-h-[85vh] flex flex-col"
                data-testid="att-bulk-approve-review-dialog"
            >
                <DialogHeader>
                    <DialogTitle>
                        Review {submittedRows.length + pendingRows.length} attendance change{(submittedRows.length + pendingRows.length) === 1 ? "" : "s"}
                    </DialogTitle>
                    <DialogDescription>
                        {submittedRows.length > 0
                            ? "Officer-submitted rows are shown with the change diff. Untick Skip to include a row in the batch."
                            : "Approve the selected rows."}
                        {pendingRows.length > 0 && submittedRows.length > 0
                            ? ` ${pendingRows.length} pending row${pendingRows.length === 1 ? "" : "s"} will be approved directly (no diff to review).`
                            : ""}
                    </DialogDescription>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto pr-1 space-y-2" data-testid="att-bulk-approve-review-body">
                    {submittedRows.map((r) => {
                        const skipped = !!skipMap[r.id];
                        return (
                            <div
                                key={r.id}
                                className="rounded-lg border p-3"
                                style={{
                                    borderColor: "var(--asl-border)",
                                    background: skipped ? "rgba(0,0,0,0.03)" : "var(--asl-navy-50)",
                                    opacity: skipped ? 0.6 : 1,
                                }}
                                data-testid={`att-bulk-approve-review-card-${r.id}`}
                            >
                                <div className="flex items-start justify-between gap-3 mb-2">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <ModuleIdChip value={r.module_employee_id} />
                                        <span className="font-semibold" style={{ color: "var(--asl-navy)" }}>
                                            {r.employee_name || "—"}
                                        </span>
                                        <span className="text-[10px] uppercase tracking-wider rounded-full px-1.5 py-0.5"
                                              style={{ background: "rgba(168, 50, 74, 0.10)", color: "var(--asl-crimson)" }}>
                                            {r.work_date}
                                        </span>
                                    </div>
                                    <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer select-none">
                                        <Checkbox
                                            checked={skipped}
                                            onCheckedChange={(c) => setSkipMap((prev) => ({ ...prev, [r.id]: !!c }))}
                                            data-testid={`att-bulk-approve-skip-${r.id}`}
                                        />
                                        Skip
                                    </label>
                                </div>
                                {r.submitted_by_officer_email && r.submitted_by_officer_at && (
                                    <div className="text-[11px] mb-2 opacity-75">
                                        Submitted by <span className="font-semibold">{r.submitted_by_officer_email}</span> on {formatDateTime(r.submitted_by_officer_at)}
                                    </div>
                                )}
                                <ChangeList entries={r.pending_changes} />
                            </div>
                        );
                    })}
                    {pendingRows.length > 0 && (
                        <div
                            className="rounded-lg border p-3 mt-2"
                            style={{ borderColor: "var(--asl-border)", background: "white" }}
                            data-testid="att-bulk-approve-direct-section"
                        >
                            <div className="text-xs font-bold mb-1.5" style={{ color: "var(--asl-navy)" }}>
                                Direct approve — {pendingRows.length} pending row{pendingRows.length === 1 ? "" : "s"}
                            </div>
                            <ul className="text-xs space-y-0.5" data-testid="att-bulk-approve-direct-list">
                                {pendingRows.map((r) => (
                                    <li key={r.id} className="flex items-center gap-2">
                                        <ModuleIdChip value={r.module_employee_id} />
                                        <span>{r.employee_name || "—"}</span>
                                        <span className="opacity-60">· {r.work_date}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        disabled={busy}
                        data-testid="att-bulk-approve-review-cancel"
                    >
                        Cancel
                    </Button>
                    <Button
                        className="asl-crimson-bg text-white hover:opacity-90"
                        onClick={() => {
                            const approveIds = [
                                ...submittedIncluded.map((r) => r.id),
                                ...pendingRows.map((r) => r.id),
                            ];
                            onConfirm(approveIds);
                        }}
                        disabled={busy || total === 0}
                        data-testid="att-bulk-approve-review-confirm"
                    >
                        {busy ? "Approving…" : `Approve ${total}`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
